import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { useAppDispatch, useAppSelector } from "../store";
import planoApi from "../api/plano.api";
import telemedicinaApi from "../api/telemedicina.api";
import type { ManchesterAnswers, ManchesterClassification, ManchesterFlowchart, PaTriageMessage, TriageAiAnalysis } from "../api/telemedicina.api";
import Section from "../components/Section";
import SectionSkeleton from "../components/SectionSkeleton";
import enqueueSnackbar from "../helpers/enqueueSnackbar";
import type { Cliente } from "../types/cliente";

/** Intervalo de polling da fila de PA (mesmo do aplicativo). */
const POLL_INTERVAL_MS = 5000;

/** Formatos aceitos pelo fynd-api/ClubeConsulta (anexos do pré-atendimento). */
const MIMES_ACEITOS = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const MAX_ANEXOS = 5;
const MAX_ANEXO_BYTES = 10 * 1024 * 1024;

const MEASURE_LABEL: Record<string, string> = {
  pas: "pressão arterial",
  temp: "temperatura",
  fc: "pulso / frequência cardíaca",
  sato2: "saturação de oxigênio",
  glicemia: "glicemia",
};

function normalizeComplaint(value: string) {
  return value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
}

// Manchester triage, followed by the doctor-gated PA queue.

function ClubeconsultaAtendimento({ cliente }: { cliente: Cliente }) {
  const [initLoading, setInitLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [atendimentoId, setAtendimentoId] = useState<number | null>(null);
  const [patientLink, setPatientLink] = useState<string | null>(null);
  const [flowcharts, setFlowcharts] = useState<ManchesterFlowchart[]>([]);
  const [flowchart, setFlowchart] = useState<ManchesterFlowchart | null>(null);
  const [query, setQuery] = useState("");
  const [showOtherSymptoms, setShowOtherSymptoms] = useState(false);
  const [complaint, setComplaint] = useState("");
  const [unknownIds, setUnknownIds] = useState<string[]>([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<ManchesterAnswers | null>(null);
  const [classification, setClassification] = useState<ManchesterClassification | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [anexos, setAnexos] = useState<{ id: string; file: File }[]>([]);
  // ── Triagem conversacional por IA ──
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState<PaTriageMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [chatDone, setChatDone] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<TriageAiAnalysis | null>(null);
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const operationRef = useRef(false);

  const reset = useCallback(() => {
    setInitLoading(true);
    setAtendimentoId(null);
    setPatientLink(null);
    setFlowchart(null);
    setClassification(null);
    setAnswers(null);
    setQuestionIndex(0);
    setQuery("");
    setShowOtherSymptoms(false);
    setComplaint("");
    setUnknownIds([]);
    setAnexos([]);
    setChatMode(false);
    setChatMessages([]);
    setChatInput("");
    setChatLoading(false);
    setChatError(null);
    setChatDone(false);
    setAiAnalysis(null);
    setReload((value) => value + 1);
  }, []);

  useEffect(() => {
    let active = true;
    setInitLoading(true);
    setInitError(null);
    async function init() {
      try {
        const aberto = await telemedicinaApi.clubeconsultaAtendimentoAberto();
        if (!active) return;
        if (aberto?.aberto && aberto.atendimentoId) {
          setAtendimentoId(aberto.atendimentoId);
          setPatientLink(aberto.status === "picked" ? aberto.patientLink || null : null);
        } else {
          const list = await telemedicinaApi.clubeconsultaFluxogramas();
          if (active) setFlowcharts(list);
        }
      } catch (error: any) {
        if (active) setInitError(error?.message || "Não foi possível preparar a triagem.");
      } finally {
        if (active) setInitLoading(false);
      }
    }
    void init();
    return () => { active = false; };
  }, [reload]);

  useEffect(() => {
    if (!atendimentoId) return;
    let active = true;
    let checking = false;
    let notifiedLink: string | null = null;
    async function verificar() {
      if (checking) return;
      checking = true;
      try {
        const res = await telemedicinaApi.clubeconsultaStatus(atendimentoId);
        if (!active) return;
        if (res.status === "picked") {
          // Keep polling while the accepted consultation link is being prepared.
          setPatientLink(res.patientLink || null);
          if (res.patientLink && res.patientLink !== notifiedLink) {
            notifiedLink = res.patientLink;
            enqueueSnackbar("Um médico aceitou o seu atendimento! Entre na consulta.", "success");
          }

        } else if (res.status === "finished") {
          active = false;
          enqueueSnackbar("Seu atendimento foi encerrado.", "info");
          reset();
        } else {
          setPatientLink(null);
          notifiedLink = null;
        }
      } catch {
        // A temporary polling failure must not reset the completed triage.
      } finally {
        checking = false;
      }
    }
    void verificar();
    const timer = setInterval(() => { if (active) void verificar(); }, POLL_INTERVAL_MS);
    return () => { active = false; clearInterval(timer); };
  }, [atendimentoId, reset]);

  function chooseFlowchart(item: ManchesterFlowchart, options: { manual?: boolean } = {}) {
    setFlowchart(item);
    setQuestionIndex(0);
    setUnknownIds([]);
    setAnswers(null);
    setClassification(null);
    setChatMessages([]);
    setChatInput("");
    setChatError(null);
    setChatDone(false);
    setAiAnalysis(null);
    if (options.manual) {
      setChatMode(false);
      return;
    }
    // Entrevista conduzida pela IA em linguagem simples; se falhar, cai nas perguntas fixas.
    setChatMode(true);
    const initial: PaTriageMessage[] = [{
      role: "user",
      content: complaint.trim() || `Meu principal sintoma é: ${item.patientLabel || item.name}.`,
    }];
    setChatMessages(initial);
    void pushChatTurn(item, initial, true);
  }

  async function pushChatTurn(item: ManchesterFlowchart, messages: PaTriageMessage[], initial = false) {
    setChatLoading(true);
    setChatError(null);
    try {
      const res = await telemedicinaApi.clubeconsultaTriagemAi({
        flowchartId: item.id,
        messages,
        patientName: cliente.nome,
      });
      setChatMessages([...messages, { role: "assistant", content: res.resposta }]);
      if (res.etapa === "classificar") setChatDone(true);
    } catch (error: any) {
      if (initial) {
        // IA indisponível: oferece as perguntas fixas, sem impedir a telemedicina.
        setChatMode(false);
        enqueueSnackbar(
          "O assistente virtual não está disponível agora. Você pode responder às perguntas fixas.",
          "warning"
        );
      } else {
        setChatError(error?.message || "Não foi possível falar com o assistente. Tente novamente.");
      }
    } finally {
      setChatLoading(false);
    }
  }

  function sendChatMessage() {
    if (!flowchart || chatLoading || !chatInput.trim()) return;
    const messages: PaTriageMessage[] = [...chatMessages, { role: "user", content: chatInput.trim() }];
    setChatMessages(messages);
    setChatInput("");
    void pushChatTurn(flowchart, messages);
  }

  /** A IA interpreta a conversa e o motor Manchester classifica o risco. */
  async function classifyFromChat() {
    if (!flowchart || operationRef.current) return;
    operationRef.current = true;
    setSubmitting(true);
    setChatError(null);
    try {
      const res = await telemedicinaApi.clubeconsultaTriagemAiClassificar({
        flowchartId: flowchart.id,
        messages: chatMessages,
        complaint: complaint.trim() || undefined,
      });
      setAnswers(res.answers);
      setClassification(res.classification);
      setAiAnalysis(res.ai ?? null);
    } catch (error: any) {
      setChatError(error?.message || "Não foi possível interpretar a conversa.");
      enqueueSnackbar(
        error?.message || "Não foi possível concluir a triagem. Tente novamente ou use as perguntas fixas.",
        "error"
      );
    } finally {
      operationRef.current = false;
      setSubmitting(false);
    }
  }

  /** Deixa a IA escolher o fluxograma a partir da queixa livre. */
  async function sugerirSintoma() {
    if (operationRef.current || !complaint.trim()) return;
    operationRef.current = true;
    setSubmitting(true);
    try {
      const res = await telemedicinaApi.clubeconsultaTriagemAiSintoma(complaint.trim());
      if (res.flowchart) {
        chooseFlowchart(res.flowchart);
      } else {
        setShowOtherSymptoms(true);
        enqueueSnackbar("Não identificamos um fluxo específico. Escolha a faixa etária para continuar.", "info");
      }
    } catch (error: any) {
      enqueueSnackbar(
        error?.message || "Não foi possível avaliar com o assistente. Escolha um sintoma da lista.",
        "error"
      );
    } finally {
      operationRef.current = false;
      setSubmitting(false);
    }
  }

  // Rolagem automática do chat.
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, chatLoading]);

  async function answerQuestion(answer: "yes" | "no" | "unknown") {
    if (!flowchart || operationRef.current) return;
    const current = flowchart.discriminators[questionIndex];
    if (!current) return;
    const pending = unknownIds.filter((id) =>
      flowchart.discriminators.slice(0, questionIndex).some((d) => d.id === id));
    if (answer === "unknown") pending.push(current.id);
    setUnknownIds(pending);
    if (answer !== "yes" && questionIndex < flowchart.discriminators.length - 1) {
      setQuestionIndex((value) => value + 1);
      return;
    }
    const data = {
      flowchartId: flowchart.id,
      confirmedIds: answer === "yes" ? [current.id] : [],
      unknownIds: pending,
    };
    operationRef.current = true;
    setSubmitting(true);
    try {
      const result = await telemedicinaApi.clubeconsultaClassificar(data);
      setAnswers(data);
      setClassification(result);
    } catch (error: any) {
      enqueueSnackbar(error?.message || "Não foi possível concluir a triagem. Tente novamente.", "error");
    } finally {
      operationRef.current = false;
      setSubmitting(false);
    }
  }

  // ── Anexos ────────────────────────────────────────────────────────────
  function handleSelecionarArquivos(files: FileList | null) {
    if (!files || !files.length) return;

    const novos: { id: string; file: File }[] = [];

    Array.from(files).forEach((file) => {
      if (anexos.length + novos.length >= MAX_ANEXOS) {
        enqueueSnackbar(`Você pode enviar no máximo ${MAX_ANEXOS} arquivos.`, "warning");
        return;
      }
      if (!MIMES_ACEITOS.includes(file.type)) {
        enqueueSnackbar(
          `Formato não suportado: ${file.name}. Envie imagens (jpg/png/webp) ou documentos (pdf/doc/docx).`,
          "error"
        );
        return;
      }
      if (file.size > MAX_ANEXO_BYTES) {
        enqueueSnackbar(`O arquivo ${file.name} excede 10MB.`, "error");
        return;
      }

      const id = String(Date.now() + Math.random());
      novos.push({ id, file });

    });

    if (novos.length) {
      setAnexos((prev) => [...prev, ...novos]);
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function handleRemoverAnexo(id: string) {
    setAnexos((prev) => prev.filter((anexo) => anexo.id !== id));
  }

  // ── Confirmar: entra na fila de PA do ClubeConsulta ───────────────────
  async function handleConfirmar() {
    if (!answers || !classification || operationRef.current || atendimentoId) return;
    operationRef.current = true;
    setSubmitting(true);
    try {
      const res = await telemedicinaApi.clubeconsultaCreateAtendimento({
        especialidade: "Pronto Atendimento",
        triagem: chatMode ? chatMessages : [],
        manchester: { ...answers, complaint: complaint.trim() || undefined },
        ia: aiAnalysis ?? undefined,
        arquivos: anexos.map((anexo) => anexo.file),
      });

      if (res.fallback) throw new Error("Serviço indisponível. Tente novamente em instantes.");
      if (!res.atendimentoId) throw new Error("Não foi possível confirmar o atendimento.");
      setAnexos([]);
      setAtendimentoId(res.atendimentoId);
    } catch (error: any) {
      enqueueSnackbar(
        error?.message || "Erro ao solicitar atendimento. Tente novamente.",
        "error"
      );
    } finally {
      operationRef.current = false;
      setSubmitting(false);
    }
  }

  // ── Cancelar solicitação na fila ──────────────────────────────────────
  async function handleCancelar() {
    if (!atendimentoId || operationRef.current) return;
    operationRef.current = true;
    setSubmitting(true);
    try {
      await telemedicinaApi.clubeconsultaCancelar(atendimentoId);
      enqueueSnackbar("Solicitação cancelada.", "info");
      reset();
    } catch (error: any) {
      enqueueSnackbar(error?.message || "Não foi possível cancelar.", "error");
    } finally {
      operationRef.current = false;
      setSubmitting(false);
    }
  }

  if (initLoading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
        <CircularProgress />
        <Typography color="text.secondary">
          Preparando seu atendimento...
        </Typography>
      </Stack>
    );
  }

  if (initError) {
    return <Alert severity="error" action={<Button onClick={reset}>Tentar novamente</Button>}>{initError}</Alert>;
  }

  const current = flowchart?.discriminators[questionIndex];
  const words = normalizeComplaint(query).split(/\s+/).filter(Boolean);
  const filtered = flowcharts.filter((item) => {
    const text = normalizeComplaint([item.name, item.patientLabel, ...(item.searchTerms || [])].join(" "));
    return words.every((word) => text.includes(word));
  }).sort((a, b) => (a.patientLabel || a.name).localeCompare(b.patientLabel || b.name, "pt-BR"));
  const generalFlowcharts = flowcharts.filter((item) => item.id.startsWith("malEstarEm"));

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Stepper activeStep={atendimentoId ? 1 : 0}>
        <Step><StepLabel>Triagem</StepLabel></Step>
        <Step><StepLabel>Consulta</StepLabel></Step>
      </Stepper>
      {atendimentoId ? (
        <Stack spacing={2}>
          <Alert severity={patientLink ? "success" : "info"} icon={<HourglassBottomIcon />}>
            {patientLink
              ? "Um médico aceitou o seu atendimento. Sua triagem já foi realizada."
              : "Triagem concluída. Aguarde um médico aceitar o atendimento para entrar na sala."}
          </Alert>
          {patientLink ? (
            <Button variant="contained" color="success" startIcon={<PlayArrowIcon />}
              onClick={() => window.open(patientLink, "_blank", "noopener,noreferrer")}>
              Entrar na consulta
            </Button>
          ) : (
            <>
              <Button variant="contained" disabled>Entrar na consulta</Button>
              <Button onClick={handleCancelar} disabled={submitting}>Cancelar solicitação</Button>
            </>
          )}
        </Stack>
      ) : (
        <>
          <Typography variant="h6">Triagem</Typography>
          <Typography variant="body2" color="text.secondary">
            Olá, {cliente.nome}. Responda à triagem antes de solicitar sua consulta.
            A sala será liberada somente quando um médico aceitar o atendimento.
          </Typography>
          <Alert severity="info">
            Se não souber responder ou não tiver como medir, escolha “Não sei informar”.
            Essa informação será sinalizada ao médico, sem impedir sua solicitação.
            Em caso de emergência, não aguarde: ligue 192 (SAMU).
          </Alert>
          <TextField label="Conte o que está sentindo (opcional)" multiline minRows={2} fullWidth
            placeholder="Descreva seus sintomas, quando começaram e o que mais deseja contar ao médico."
            value={complaint} onChange={(event) => setComplaint(event.target.value)}
            disabled={submitting} inputProps={{ maxLength: 2000 }} />
          {!flowchart && (
            <Button variant="contained" disabled={submitting || !complaint.trim()} onClick={sugerirSintoma}>
              Avaliar com assistente (IA)
            </Button>
          )}
          {!flowchart ? (
            <>
              <TextField label="Buscar sintoma" placeholder="Ex.: dor de cabeça, dor na barriga, falta de ar"
                value={query} onChange={(event) => setQuery(event.target.value)} fullWidth />
              <Stack spacing={1} sx={{ maxHeight: 380, overflowY: "auto" }}>
                {filtered.map((item) => (
                  <Button key={item.id} variant="outlined" sx={{ justifyContent: "flex-start" }}
                    onClick={() => chooseFlowchart(item)}>
                    {item.patientLabel || item.name}
                  </Button>
                ))}
                {!filtered.length && <Typography>Não encontrou? Use a opção de outros sintomas abaixo.</Typography>}
              </Stack>
              <Button onClick={() => setShowOtherSymptoms((value) => !value)}>Não encontrei meu sintoma</Button>
              {showOtherSymptoms && (
                <Stack spacing={1}>
                  <Typography>Descreva acima o que está sentindo e escolha a faixa etária para continuar com as perguntas gerais.</Typography>
                  {generalFlowcharts.map((item) => (
                    <Button key={item.id} variant="outlined" onClick={() => chooseFlowchart(item)}>{item.name}</Button>
                  ))}
                </Stack>
              )}
            </>
          ) : !classification ? (
            chatMode ? (
              <Stack spacing={2}>
                <Typography fontWeight="bold">{flowchart.patientLabel || flowchart.name}</Typography>
                <Paper variant="outlined" sx={{ p: 2, maxHeight: 360, overflowY: "auto" }}>
                  <Stack spacing={1.5}>
                    {chatMessages.map((message, index) => (
                      <Box key={index} sx={{ display: "flex", justifyContent: message.role === "user" ? "flex-end" : "flex-start" }}>
                        <Box sx={{
                          maxWidth: "85%", px: 1.5, py: 1, borderRadius: 2, whiteSpace: "pre-wrap",
                          bgcolor: message.role === "user" ? "primary.main" : "action.hover",
                          color: message.role === "user" ? "primary.contrastText" : "text.primary",
                        }}>
                          <Typography variant="body2">{message.content}</Typography>
                        </Box>
                      </Box>
                    ))}
                    {chatLoading && <CircularProgress size={20} />}
                    <div ref={chatEndRef} />
                  </Stack>
                </Paper>
                {chatError && <Alert severity="error">{chatError}</Alert>}
                {!chatDone && (
                  <Stack direction="row" spacing={1}>
                    <TextField fullWidth size="small" placeholder="Escreva sua resposta..."
                      value={chatInput} onChange={(event) => setChatInput(event.target.value)}
                      onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); sendChatMessage(); } }}
                      disabled={chatLoading} inputProps={{ maxLength: 2000 }} />
                    <Button variant="contained" onClick={sendChatMessage} disabled={chatLoading || !chatInput.trim()}>Enviar</Button>
                  </Stack>
                )}
                {chatDone && (
                  <Button variant="contained" disabled={submitting} onClick={classifyFromChat}>Ver minha classificação</Button>
                )}
                {!chatDone && chatMessages.filter((message) => message.role === "user").length >= 2 && (
                  <Button variant="outlined" disabled={submitting || chatLoading} onClick={classifyFromChat}>
                    Encerrar e classificar
                  </Button>
                )}
                <Button disabled={submitting || chatLoading} onClick={() => chooseFlowchart(flowchart, { manual: true })}>
                  Prefiro responder perguntas fixas
                </Button>
                <Button disabled={submitting || chatLoading} onClick={() => setFlowchart(null)}>Alterar sintoma</Button>
              </Stack>
            ) : (
            <Stack spacing={2}>
              <Typography fontWeight="bold">{flowchart.patientLabel || flowchart.name}</Typography>
              <Typography variant="body2">Pergunta {questionIndex + 1} de {flowchart.discriminators.length}</Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography>{current?.text}</Typography>
                {current?.measure && (
                  <Typography variant="caption" color="text.secondary">
                    Pode exigir medição de {MEASURE_LABEL[current.measure] || current.measure}. Não precisa ter um aparelho: se não souber, escolha “Não sei informar”.
                  </Typography>
                )}
              </Paper>
              <Stack direction="row" spacing={2}>
                <Button fullWidth variant="outlined" disabled={submitting} onClick={() => answerQuestion("no")}>Não</Button>
                <Button fullWidth variant="contained" disabled={submitting} onClick={() => answerQuestion("yes")}>Sim</Button>
              </Stack>
              <Button disabled={submitting} onClick={() => answerQuestion("unknown")}>Não sei informar</Button>
              <Button disabled={submitting} onClick={() => {
                if (questionIndex > 0) setQuestionIndex((value) => value - 1);
                else setFlowchart(null);
              }}>Voltar</Button>
            </Stack>
            )
          ) : (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2, borderLeft: 6, borderColor: classification.requiresReview && !classification.emergency ? "warning.main" : classification.color }}>
                <Typography variant="h6">{classification.requiresReview && !classification.emergency ? "Triagem registrada — requer avaliação do médico" : classification.levelLabel}</Typography>
                <Typography>{flowchart.patientLabel || classification.flowchartName}</Typography>
                {classification.discriminatorText && <Typography variant="body2">{classification.discriminatorText}</Typography>}
              </Paper>
              {aiAnalysis?.summary && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2">Resumo da conversa (IA)</Typography>
                  <Typography variant="body2">{aiAnalysis.summary}</Typography>
                  {!!aiAnalysis.redFlags?.length && (
                    <Typography variant="body2" color="error.main">
                      Sinais de alerta: {aiAnalysis.redFlags.join("; ")}
                    </Typography>
                  )}
                </Paper>
              )}
              {classification.requiresReview && <Alert severity="warning">
                Há respostas que você não soube informar. Não é possível descartar maior urgência.
                O médico receberá essas pendências. Se estiver piorando ou com sintomas graves, procure atendimento presencial imediatamente.
              </Alert>}
              {classification.emergency ? (
                <Alert severity="error">Emergência: não espere pela videochamada. Vá a um pronto-socorro ou ligue 192 (SAMU) agora.</Alert>
              ) : classification.level === "orange" ? (
                <Alert severity="warning">Situação muito urgente. Procure atendimento em até 10 minutos. Se os sintomas piorarem, ligue 192 (SAMU).</Alert>
              ) : classification.level === "yellow" ? (
                <Alert severity="warning">Situação urgente. Procure atendimento em até 60 minutos. Não aguarde na fila se os sintomas piorarem.</Alert>
              ) : null}
              <Typography variant="caption" color="text.secondary">
                Esta classificação é orientativa e não substitui a avaliação de um profissional de saúde.
              </Typography>
              <Button disabled={submitting} onClick={() => {
                setFlowchart(null); setClassification(null); setAnswers(null); setQuestionIndex(0); setUnknownIds([]);
                setChatMode(false); setChatMessages([]); setChatInput(""); setChatError(null); setChatDone(false); setAiAnalysis(null);
              }}>Refazer triagem</Button>
            </Stack>
          )}
          <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx" multiple hidden
            onChange={(event) => handleSelecionarArquivos(event.target.files)} />
          <Button startIcon={<AttachFileIcon />} onClick={() => fileInputRef.current?.click()} disabled={submitting}>
            Anexar exames ou documentos
          </Button>
          {anexos.map((anexo) => (
            <Stack key={anexo.id} direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" sx={{ flex: 1, overflowWrap: "anywhere" }}>{anexo.file.name}</Typography>
              <IconButton aria-label={`Remover ${anexo.file.name}`} disabled={submitting} onClick={() => handleRemoverAnexo(anexo.id)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          ))}
          {classification && <Button variant="contained" disabled={submitting} onClick={handleConfirmar}>Solicitar consulta</Button>}
          {submitting && <CircularProgress size={24} />}
        </>
      )}
    </Stack>
  );
}

// ─── Página ──────────────────────────────────────────────────────────────────

export default function TelemedicinaPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    loading: loadingCliente,
    cliente,
    plano,
  } = useAppSelector((state) => state.cliente);
  const [loadingPlano, setLoadingPlano] = useState<boolean>(true);

  const loadPlano = async (abortController?: AbortController) => {
    try {
      if (plano) {
        setLoadingPlano(false);

        return;
      }

      if (loadingCliente || !cliente) {
        return;
      }

      setLoadingPlano(true);

      const clientePlano = await planoApi.findById(
        cliente.planoId,
        abortController
      );

      dispatch({ type: "SET_CLIENTE_PLANO", payload: clientePlano });
    } catch (error: any) {
      if (error?.code === "ERR_CANCELED") {
        return;
      }

      enqueueSnackbar(
        error?.message || "Não foi possível obter as informações do seu plano.",
        "error"
      );
    } finally {
      setLoadingPlano(false);
    }
  };

  useEffect(() => {
    const abortController = new AbortController();

    if (plano) {
      setLoadingPlano(false);
    }

    if (!loadingCliente && cliente && !plano) {
      loadPlano(abortController);
    }

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCliente, cliente, plano]);

  const renderConteudo = () => {
    if (loadingCliente || loadingPlano) {
      return <SectionSkeleton />;
    }

    if (!plano || !plano.telemedicina) {
      return (
        <Section
          sx={{
            display: "flex",
            flexFlow: "column nowrap",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography sx={{ textAlign: "center" }}>
            Parece que o serviço de telemedicina não está incluso no seu plano.
          </Typography>

          <Typography sx={{ fontWeight: "bold", textAlign: "center" }}>
            Assine já e tenha acesso imediato a todos os benefícios!
          </Typography>

          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => navigate("/app/assinar")}
          >
            Quero Assinar
          </Button>
        </Section>
      );
    }

    // Fluxo primário: ClubeConsulta (PA virtual).
    return (
      <Section sx={{ width: "100%", maxWidth: { xs: "100%", md: 720 } }}>
        {cliente ? (
          <ClubeconsultaAtendimento
            cliente={cliente}
          />
        ) : (
          <CircularProgress />
        )}
      </Section>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexFlow: "column nowrap",
        alignItems: "center",
        gap: 2,
        height: "100%",
      }}
    >
      <Box sx={{ textAlign: "center", pb: 2 }}>
        <Typography variant="h4">Telemedicina</Typography>
        <Typography>Tenha rapidez e segurança no seu atendimento.</Typography>
      </Box>

      {renderConteudo()}
    </Box>
  );
}
